/*
 * Story bubbles + Instagram-style story player
 * - Each bubble is a group of clips; progress bars show the clips in the current group.
 * - Tap right / clip ends -> next clip, then next group. Tap left -> previous clip.
 * - Swipe left/right (or desktop arrows) -> skip a whole group. Swipe down / Esc / X -> close.
 * - Press and hold -> pause. Back button on mobile closes the player.
 * - Seen state is kept only in this browser (localStorage).
 * - Fires Shopify custom pixel events + Meta Pixel event on open.
 */
(() => {
  if (customElements.get('story-bubbles')) return;

  const SEEN_KEY = 'storyBubbles:seen:v1';
  const HOLD_MS = 220;
  const SWIPE_X = 50;
  const CLOSE_Y = 110;
  const SEEN_RATIO = 0.9;

  /* ---------- seen state ---------- */
  const seenStore = {
    read() {
      try {
        return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'));
      } catch (e) {
        return new Set();
      }
    },
    add(id) {
      try {
        const ids = Array.from(this.read());
        if (ids.includes(String(id))) return;
        ids.push(String(id));
        localStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(-500)));
      } catch (e) {
        /* storage unavailable: seen state just won't persist */
      }
    },
    has(id) {
      return this.read().has(String(id));
    },
  };

  /* ---------- tracking ---------- */
  function track(name, detail) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, detail);
      }
    } catch (e) {}
  }
  function trackMeta(name, detail) {
    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', name, detail);
    } catch (e) {}
  }

  const ICON = {
    close: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    soundOff:
      '<svg class="icon-off" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M22 9l-6 6M16 9l6 6"/></svg>',
    soundOn:
      '<svg class="icon-on" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg>',
  };

  /* ---------- player ---------- */
  class StoryPlayer {
    constructor(host, data) {
      this.host = host;
      this.groups = data.groups;
      this.t = data.strings;
      this.productId = host.dataset.productId;
      this.g = 0;
      this.c = 0;
      this.muted = true;
      this.isOpen = false;
      this.raf = null;
      this.pointer = null;
      this.build();
    }

    build() {
      const el = document.createElement('div');
      el.className = 'story-player';
      el.hidden = true;
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.innerHTML = `
        <button type="button" class="story-player__nav story-player__nav--prev" aria-label="${this.t.prev}">${ICON.prev}</button>
        <div class="story-player__stage">
          <video class="story-player__video" playsinline webkit-playsinline muted preload="auto"></video>
          <div class="story-player__spinner" hidden></div>
          <p class="story-player__error" hidden>${this.t.error}</p>
          <div class="story-player__shade"></div>
          <div class="story-player__top">
            <div class="story-player__bars"></div>
            <div class="story-player__header">
              <img class="story-player__avatar" alt="" hidden>
              <p class="story-player__title"></p>
              <button type="button" class="story-player__btn story-player__mute" aria-pressed="false" aria-label="${this.t.soundOn}">${ICON.soundOff}${ICON.soundOn}</button>
              <button type="button" class="story-player__btn story-player__close" aria-label="${this.t.close}">${ICON.close}</button>
            </div>
          </div>
        </div>
        <button type="button" class="story-player__nav story-player__nav--next" aria-label="${this.t.next}">${ICON.next}</button>
      `;
      document.body.appendChild(el);

      this.el = el;
      this.stage = el.querySelector('.story-player__stage');
      this.video = el.querySelector('.story-player__video');
      this.spinner = el.querySelector('.story-player__spinner');
      this.errorEl = el.querySelector('.story-player__error');
      this.barsEl = el.querySelector('.story-player__bars');
      this.avatar = el.querySelector('.story-player__avatar');
      this.title = el.querySelector('.story-player__title');
      this.muteBtn = el.querySelector('.story-player__mute');
      this.closeBtn = el.querySelector('.story-player__close');
      this.navPrev = el.querySelector('.story-player__nav--prev');
      this.navNext = el.querySelector('.story-player__nav--next');
      this.preloader = document.createElement('video');
      this.preloader.muted = true;
      this.preloader.preload = 'auto';

      this.closeBtn.addEventListener('click', () => this.close());
      this.muteBtn.addEventListener('click', () => this.toggleMute());
      this.navPrev.addEventListener('click', () => this.goGroup(-1));
      this.navNext.addEventListener('click', () => this.goGroup(1));
      el.addEventListener('click', (e) => {
        if (e.target === el) this.close();
      });

      this.video.addEventListener('waiting', () => (this.spinner.hidden = false));
      this.video.addEventListener('playing', () => {
        this.spinner.hidden = true;
        this.errorEl.hidden = true;
      });
      this.video.addEventListener('canplay', () => (this.spinner.hidden = true));
      this.video.addEventListener('ended', () => {
        this.markSeen();
        this.next();
      });
      this.video.addEventListener('timeupdate', () => {
        const v = this.video;
        if (v.duration && v.currentTime / v.duration >= SEEN_RATIO) this.markSeen();
      });
      this.video.addEventListener('error', () => {
        if (!this.isOpen || !this.video.getAttribute('src')) return;
        this.spinner.hidden = true;
        this.errorEl.hidden = false;
      });

      this.stage.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      this.stage.addEventListener('pointermove', (e) => this.onPointerMove(e));
      this.stage.addEventListener('pointerup', (e) => this.onPointerUp(e));
      this.stage.addEventListener('pointercancel', () => this.resetDrag());
      this.stage.addEventListener('contextmenu', (e) => e.preventDefault());

      this.onKey = this.onKey.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.tick = this.tick.bind(this);
    }

    /* ----- open / close ----- */
    open(gPos, opener) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.opener = opener || null;
      this.g = gPos;
      this.c = this.firstUnseen(gPos);

      document.documentElement.classList.add('story-player-open');
      this.el.hidden = false;
      requestAnimationFrame(() => this.el.classList.add('is-visible'));

      document.addEventListener('keydown', this.onKey);
      document.addEventListener('visibilitychange', this.onVisibility);
      try {
        history.pushState({ storyPlayer: true }, '');
        window.addEventListener('popstate', this.onPopState);
        this.pushedState = true;
      } catch (e) {
        this.pushedState = false;
      }

      this.renderGroup();
      this.playClip();
      this.closeBtn.focus({ preventScroll: true });
      this.raf = requestAnimationFrame(this.tick);

      const group = this.groups[this.g];
      const detail = { product_id: this.productId, group: group.label, group_handle: group.handle };
      track('story_open', detail);
      trackMeta('StoryOpen', detail);
    }

    close(fromPopState) {
      if (!this.isOpen) return;
      this.isOpen = false;

      cancelAnimationFrame(this.raf);
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.preloader.removeAttribute('src');

      this.el.classList.remove('is-visible', 'is-dragging');
      this.stage.style.transform = '';
      this.el.hidden = true;
      document.documentElement.classList.remove('story-player-open');

      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('popstate', this.onPopState);
      if (!fromPopState && this.pushedState && history.state && history.state.storyPlayer) {
        history.back();
      }

      this.host.refreshSeen();
      if (this.opener) this.opener.focus({ preventScroll: true });
      track('story_close', { product_id: this.productId, group: this.groups[this.g].label, clip_index: this.c });
    }

    onPopState() {
      this.close(true);
    }

    /* ----- rendering ----- */
    renderGroup() {
      const group = this.groups[this.g];
      this.el.setAttribute('aria-label', group.label);
      this.title.textContent = group.label;
      if (group.avatar) {
        this.avatar.src = group.avatar;
        this.avatar.hidden = false;
      } else {
        this.avatar.hidden = true;
      }
      this.barsEl.innerHTML = group.clips
        .map(() => '<span class="story-player__bar"><span class="story-player__bar-fill"></span></span>')
        .join('');
      this.fills = Array.from(this.barsEl.querySelectorAll('.story-player__bar-fill'));
      this.navPrev.hidden = this.g === 0;
      this.navNext.hidden = this.g === this.groups.length - 1;
    }

    paintBars(progress) {
      this.fills.forEach((fill, i) => {
        const value = i < this.c ? 1 : i > this.c ? 0 : progress;
        fill.style.transform = `scaleX(${value})`;
      });
    }

    tick() {
      if (!this.isOpen) return;
      const v = this.video;
      const progress = v.duration ? Math.min(v.currentTime / v.duration, 1) : 0;
      this.paintBars(progress);
      this.raf = requestAnimationFrame(this.tick);
    }

    playClip() {
      const clip = this.groups[this.g].clips[this.c];
      this.errorEl.hidden = true;
      this.spinner.hidden = false;
      this.paintBars(0);

      this.video.poster = clip.poster || '';
      this.video.muted = this.muted;
      this.video.src = clip.src;
      this.play();
      this.preloadNext();

      track('story_clip_view', {
        product_id: this.productId,
        group: this.groups[this.g].label,
        clip_index: this.c,
        clip_count: this.groups[this.g].clips.length,
      });
    }

    play() {
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch((err) => {
          if (err && err.name === 'NotAllowedError' && !this.video.muted) {
            this.setMuted(true);
            this.video.play().catch(() => {});
          }
        });
      }
    }

    preloadNext() {
      const nextClip = this.peekNext();
      if (nextClip && nextClip.src) {
        if (this.preloader.getAttribute('src') !== nextClip.src) this.preloader.src = nextClip.src;
      } else {
        this.preloader.removeAttribute('src');
      }
    }

    peekNext() {
      const clips = this.groups[this.g].clips;
      if (this.c < clips.length - 1) return clips[this.c + 1];
      const nextGroup = this.groups[this.g + 1];
      return nextGroup ? nextGroup.clips[this.firstUnseen(this.g + 1)] : null;
    }

    /* ----- navigation ----- */
    next() {
      const clips = this.groups[this.g].clips;
      if (this.c < clips.length - 1) {
        this.c += 1;
        this.playClip();
        return;
      }
      track('story_complete', { product_id: this.productId, group: this.groups[this.g].label });
      if (this.g < this.groups.length - 1) {
        this.g += 1;
        this.c = this.firstUnseen(this.g);
        this.renderGroup();
        this.playClip();
      } else {
        this.close();
      }
    }

    prev() {
      if (this.c > 0) {
        this.c -= 1;
        this.playClip();
      } else if (this.g > 0) {
        this.g -= 1;
        this.c = this.groups[this.g].clips.length - 1;
        this.renderGroup();
        this.playClip();
      } else {
        this.video.currentTime = 0;
        this.play();
      }
    }

    goGroup(dir) {
      const target = this.g + dir;
      if (target < 0) return;
      if (target >= this.groups.length) {
        this.close();
        return;
      }
      this.g = target;
      this.c = this.firstUnseen(target);
      this.renderGroup();
      this.playClip();
    }

    firstUnseen(gPos) {
      const clips = this.groups[gPos].clips;
      const seen = seenStore.read();
      const idx = clips.findIndex((clip) => !seen.has(String(clip.id)));
      return idx === -1 ? 0 : idx;
    }

    markSeen() {
      const clip = this.groups[this.g] && this.groups[this.g].clips[this.c];
      if (clip) seenStore.add(clip.id);
    }

    /* ----- sound ----- */
    toggleMute() {
      this.setMuted(!this.muted);
      if (!this.muted) this.play();
    }

    setMuted(muted) {
      this.muted = muted;
      this.video.muted = muted;
      this.el.classList.toggle('is-unmuted', !muted);
      this.muteBtn.setAttribute('aria-pressed', String(!muted));
      this.muteBtn.setAttribute('aria-label', muted ? this.t.soundOn : this.t.soundOff);
    }

    /* ----- gestures ----- */
    onPointerDown(e) {
      if (e.target.closest('button')) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.pointer = { id: e.pointerId, x: e.clientX, y: e.clientY, held: false, dragging: false, axis: null };
      try {
        this.stage.setPointerCapture(e.pointerId);
      } catch (err) {}
      this.holdTimer = setTimeout(() => {
        if (!this.pointer || this.pointer.axis) return;
        this.pointer.held = true;
        this.video.pause();
      }, HOLD_MS);
    }

    onPointerMove(e) {
      const p = this.pointer;
      if (!p || p.id !== e.pointerId) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (!p.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 10) {
        p.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
        clearTimeout(this.holdTimer);
        if (p.held) {
          p.held = false;
          this.play();
        }
      }
      if (p.axis === 'y' && dy > 0) {
        p.dragging = true;
        this.el.classList.add('is-dragging');
        this.stage.style.transform = `translateY(${dy}px)`;
      }
    }

    onPointerUp(e) {
      const p = this.pointer;
      if (!p || p.id !== e.pointerId) return;
      clearTimeout(this.holdTimer);
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      this.pointer = null;

      if (p.held) {
        this.play();
        return;
      }
      if (p.axis === 'y') {
        if (dy > CLOSE_Y) {
          this.close();
        } else {
          this.resetDrag();
        }
        return;
      }
      if (p.axis === 'x') {
        if (Math.abs(dx) > SWIPE_X) this.goGroup(dx < 0 ? 1 : -1);
        return;
      }
      const rect = this.stage.getBoundingClientRect();
      if (e.clientX - rect.left < rect.width / 3) {
        this.prev();
      } else {
        this.markSeen();
        this.next();
      }
    }

    resetDrag() {
      clearTimeout(this.holdTimer);
      this.pointer = null;
      this.el.classList.remove('is-dragging');
      this.stage.style.transform = '';
    }

    /* ----- keyboard / focus ----- */
    onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.markSeen();
        this.next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'Tab') {
        const focusables = Array.from(this.el.querySelectorAll('button')).filter(
          (b) => !b.hidden && b.offsetParent !== null
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (!this.el.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    onVisibility() {
      if (document.hidden) {
        this.video.pause();
      } else if (this.isOpen) {
        this.play();
      }
    }
  }

  /* ---------- bubbles element ---------- */
  class StoryBubbles extends HTMLElement {
    connectedCallback() {
      if (this.player) return;
      const dataEl = this.querySelector('script[data-story-data]');
      if (!dataEl) return;
      let data;
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      if (!data.groups || !data.groups.length) return;

      this.data = data;
      this.bubbles = Array.from(this.querySelectorAll('[data-group-index]'));
      this.refreshSeen();
      this.loadFrames();

      this.bubbles.forEach((bubble) => {
        bubble.addEventListener('click', () => {
          const index = Number(bubble.dataset.groupIndex);
          const pos = data.groups.findIndex((group) => group.index === index);
          if (pos === -1) return;
          if (!this.player) this.player = new StoryPlayer(this, data);
          this.player.open(pos, bubble);
        });
      });
    }

    /* Bubble thumbnails: load a real frame from the clip once the bubbles are near the viewport. */
    loadFrames() {
      const frames = Array.from(this.querySelectorAll('video[data-frame-src]'));
      if (!frames.length) return;
      const conn = navigator.connection;
      if (conn && conn.saveData) return;

      const load = (video) => {
        if (video.dataset.loaded) return;
        video.dataset.loaded = '1';
        const show = () => video.classList.add('is-ready');
        video.addEventListener('seeked', show, { once: true });
        video.addEventListener('loadeddata', show, { once: true });
        video.addEventListener('error', () => video.remove(), { once: true });
        video.muted = true;
        video.preload = 'metadata';
        video.src = video.dataset.frameSrc;
        video.load();
      };

      if (!('IntersectionObserver' in window)) {
        frames.forEach(load);
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);
            load(entry.target);
          });
        },
        { rootMargin: '200px' }
      );
      frames.forEach((frame) => io.observe(frame));
    }

    refreshSeen() {
      const seen = seenStore.read();
      this.bubbles.forEach((bubble) => {
        const index = Number(bubble.dataset.groupIndex);
        const group = this.data.groups.find((g) => g.index === index);
        const allSeen = group && group.clips.every((clip) => seen.has(String(clip.id)));
        bubble.classList.toggle('is-seen', Boolean(allSeen));
      });
    }
  }

  customElements.define('story-bubbles', StoryBubbles);
})();
